/* eslint-disable prettier/prettier */
import { GridOptions, ValueFormatterParams } from 'ag-grid-community'
import {
  ColumnType,
  columnTypes,
  defaultOnCellClicked,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import { DefaultCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import { ClientSideNumberFilter } from '../../../containers/BulkSheetView/components/filter'
import _ from 'lodash'
import { intl } from '../../../../i18n'
import { SequenceNoCellRenderer } from '../../../containers/commons/AgGrid/components/cell/custom/sequenceNo/SequenceNoCellRenderer'
import { floatValueFormatter } from '../../../page-properties/bulksheet-properties/common'
import numberCellStyle from '../../../containers/commons/AgGrid/components/cell/common/number/cellStyle'
import { RequestStatus } from '../taskActualWorkApprovalStatus'
import UserLinkIconCellRenderer from '../../../containers/BulkSheetView/components/cellRenderer/UserLinkCellRenderer'
import { DISCREPANCY_CELL_STYLE } from '../../ActualWorkingHours/gridOptions'
import { DecimalAndSexagesimalScale } from '../../../../lib/functions/decimalAndSexagesimalScale'
import { convertDecimalHourToHM } from '../../../../utils/time'

export const numberFormatter = (params: ValueFormatterParams) => {
  // if (isTotalRow(params.data)) return ''
  if (params.value === undefined || params.value === null) {
    return '0'
  }
  return params.value
}

export const floatFormatter = (params: ValueFormatterParams) => {
  if (params.value === undefined || params.value === null) return ''

  // Check if context has decimalScaleUnit setting
  if (
    params.context?.decimalScaleUnit === DecimalAndSexagesimalScale.SEXAGESIMAL
  ) {
    return convertDecimalHourToHM(params.value) || ''
  }

  return floatValueFormatter(params, '', 2)
}

const percentageFormatter = (params: ValueFormatterParams) => {
  // if (isTotalRow(params.data)) return ''
  if (params.value === undefined || params.value === null) return ''
  const value = parseFloat(params.value)
  if (!isFinite(value)) return ''
  return `${value.toFixed(1)}%`
}
// Unnecessary, but may be reimplemented in the future.
// const isTotalRow = (
//   data: TaskActualWorkApprovalStatusRow | TaskActualWorkApprovalStatusTotalRow
// ): boolean => {
//   return data?.type === TaskActualWorkApprovalStatusRowType.TOTAL
// }

export const getRequestStatusLabel = (status: RequestStatus): string => {
  switch (status) {
    case RequestStatus.APPROVED:
      return intl.formatMessage({
        id: 'taskActualWorkApprovalStatus.approved',
      })
    case RequestStatus.UNAPPROVED:
      return intl.formatMessage({
        id: 'taskActualWorkApprovalStatus.unapproved',
      })
    case RequestStatus.UNREQUESTED:
      return intl.formatMessage({
        id: 'taskActualWorkApprovalStatus.unrequested',
      })
    default:
      return '-'
  }
}
export const TaskActualWorkApprovalStatusGridOptions = (): GridOptions => {
  return {
    groupHeaderHeight: 25,
    headerHeight: 45,
    floatingFiltersHeight: 25,
    rowHeight: 32,
    treeData: false,
    context: {},
    suppressLastEmptyLineOnPaste: true,
    suppressContextMenu: true,
    // Row
    rowDragManaged: false,
    rowDragMultiRow: false,
    suppressMoveWhenRowDragging: false,
    enterMovesDownAfterEdit: true,
    // Column
    columnTypes: columnTypes(),
    components: frameworkComponents,
    defaultColDef: {
      width: 75,
      editable: false,
      enableValue: false,
      sortable: true,
      resizable: true,
      suppressMenu: true,
      suppressSizeToFit: true,
      singleClickEdit: false,
      cellEditor: 'textEditor',
      cellRenderer: DefaultCellRenderer,
      onCellClicked: defaultOnCellClicked,
      suppressPaste: params => !_.isEmpty(params.context?.copied),
    },
    columnDefs: [
      {
        field: 'rowNumber',
        type: [ColumnType.sequenceNo],
        resizable: false,
        width: 35,
        cellRenderer: SequenceNoCellRenderer,
        valueGetter: params => {
          // if (isTotalRow(params.data)) return ''
          return (params.node?.rowIndex ?? 0) + 1
        },
      },
      {
        field: 'companyName',
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.companyName',
        }),
        hide: false,
        cellStyle: { justifyContent: 'left' },
        width: 110,
        pinned: true,
        lockPosition: 'left',
        filter: 'agSetColumnFilter',
        floatingFilter: true,
      },
      {
        field: 'division',
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.division',
        }),
        hide: false,
        cellStyle: { justifyContent: 'left' },
        width: 95,
        pinned: true,
        lockPosition: 'left',
        filter: 'agSetColumnFilter',
        floatingFilter: true,
      },
      {
        field: 'position',
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.position',
        }),
        hide: false,
        cellStyle: { justifyContent: 'left' },
        width: 95,
        pinned: true,
        lockPosition: 'left',
        filter: 'agSetColumnFilter',
        floatingFilter: true,
      },
      {
        field: 'partnerMember',
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.employeeType',
        }),
        hide: false,
        cellStyle: { justifyContent: 'left' },
        width: 120,
        pinned: true,
        lockPosition: 'left',
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        valueGetter: params => {
          // if (isTotalRow(params.data)) return ''
          return params.data?.partnerMember
            ? intl.formatMessage({
                id: 'taskActualWorkApprovalStatus.businessPartner',
              })
            : intl.formatMessage({
                id: 'taskActualWorkApprovalStatus.fullTimeEmployee',
              })
        },
      },
      {
        field: 'userName',
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.userName',
        }),
        hide: false,
        cellStyle: { justifyContent: 'left' },
        width: 120,
        cellRenderer: UserLinkIconCellRenderer,
        pinned: true,
        lockPosition: 'left',
        filter: 'agSetColumnFilter',
        floatingFilter: true,
      },
      {
        field: 'requestStatus',
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.approvalStatus',
        }),
        hide: false,
        width: 110,
        pinned: true,
        lockPosition: 'left',
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        cellStyle: params =>
          params.data?.requestStatus &&
          (params.data.requestStatus === RequestStatus.UNAPPROVED ||
            params.data.requestStatus === RequestStatus.UNREQUESTED)
            ? { ...DISCREPANCY_CELL_STYLE, justifyContent: 'left' }
            : { justifyContent: 'left' },
        valueGetter: params => {
          return getRequestStatusLabel(params.data?.requestStatus)
        },
      },
      {
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.inputConfirmation',
        }),
        children: [
          {
            field: 'discrepancyActualWorkingHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.actualWorkingHours',
            }),
            hide: false,
            width: 75,
            pinned: true,
            lockPosition: 'left',
            filter: 'agSetColumnFilter',
            floatingFilter: true,
            cellStyle: params => {
              // if (isTotalRow(params.data)) return { justifyContent: 'center' }
              return params.data?.discrepancyActualWorkingHour
                ? { ...DISCREPANCY_CELL_STYLE, justifyContent: 'left' }
                : { justifyContent: 'left' }
            },
            valueGetter: params => {
              // if (isTotalRow(params.data)) {
              //   return intl.formatMessage(
              //     {
              //       id: 'taskActualWorkApprovalStatus.totalNotEntered',
              //     },
              //     {
              //       count: params.data.totalActualWorkingHourNotEntered,
              //     }
              //   )
              // }
              return params.data?.discrepancyActualWorkingHour
                ? intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.blank',
                  })
                : intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.entered',
                  })
            },
          },
          {
            field: 'discrepancyTaskActualWork',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.taskActualWorkHours',
            }),
            hide: false,
            width: 100,
            pinned: true,
            lockPosition: 'left',
            filter: 'agSetColumnFilter',
            floatingFilter: true,
            cellStyle: params => {
              // if (isTotalRow(params.data)) return { justifyContent: 'center' }
              return params.data?.discrepancyTaskActualWork
                ? { ...DISCREPANCY_CELL_STYLE, justifyContent: 'left' }
                : { justifyContent: 'left' }
            },
            valueGetter: params => {
              // if (isTotalRow(params.data)) {
              //   return intl.formatMessage(
              //     {
              //       id: 'taskActualWorkApprovalStatus.totalNotEntered',
              //     },
              //     {
              //       count: params.data.totalTaskActualWorkNotEntered,
              //     }
              //   )
              // }
              return params.data?.discrepancyTaskActualWork
                ? intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.blank',
                  })
                : intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.entered',
                  })
            },
          },
        ],
      },
      {
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.discrepancyVerification',
        }),
        children: [
          {
            field: 'discrepancyBetweenActualWorkingHourAndTaskActualWork',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.discrepancyBetweenActualWorkingHourAndTaskActualWork',
            }),
            hide: false,
            width: 110,
            pinned: true,
            lockPosition: 'left',
            filter: 'agSetColumnFilter',
            floatingFilter: true,
            cellStyle: params => {
              // if (isTotalRow(params.data)) return { justifyContent: 'center' }
              return params.data
                ?.discrepancyBetweenActualWorkingHourAndTaskActualWork
                ? { ...DISCREPANCY_CELL_STYLE, justifyContent: 'left' }
                : { justifyContent: 'left' }
            },
            valueGetter: params => {
              // if (isTotalRow(params.data)) {
              //   return intl.formatMessage(
              //     {
              //       id: 'taskActualWorkApprovalStatus.totalDiscrepancy',
              //     },
              //     {
              //       count: params.data.totalDiscrepancyBetweenActualWorkingHourAndTaskActualWork,
              //     }
              //   )
              // }
              return params.data
                ?.discrepancyBetweenActualWorkingHourAndTaskActualWork
                ? intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.divergence',
                  })
                : intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.notDivergence',
                  })
            },
          },
        ],
      },
      // all projects
      {
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.statusForAllProjects',
        }),
        children: [
          {
            field: 'scheduledWorkingDays',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.workingDays',
            }),
            hide: false,
            width: 75,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: numberFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'absences',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.holidayDays',
            }),
            hide: false,
            width: 75,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: numberFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'workingDays',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.attendanceDays',
            }),
            hide: false,
            width: 75,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: numberFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'totalWorkingTime',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.attendanceTime',
            }),
            hide: false,
            width: 75,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: floatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'totalActualWorkingHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.actualTime',
            }),
            hide: false,
            width: 75,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: floatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'requestedHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.requestedTime',
            }),
            hide: false,
            width: 75,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: floatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'unRequestedHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.unRequestedTime',
            }),
            hide: false,
            width: 85,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: floatFormatter,
            cellStyle: numberCellStyle,
          },
        ],
      },
      // thisProject date
      {
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.requestedDateForThisProject',
        }),
        children: [
          {
            field: 'actualWorkingDaysCount',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.actualDate',
            }),
            hide: false,
            width: 75,
            filter: 'agSetColumnFilter',
            floatingFilter: true,
            valueFormatter: numberFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'requestedDaysCount',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.requestedDate',
            }),
            hide: false,
            width: 75,
            filter: 'agSetColumnFilter',
            floatingFilter: true,
            valueFormatter: numberFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'unRequestedDaysCount',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.unRequestedDate',
            }),
            hide: false,
            width: 85,
            filter: 'agSetColumnFilter',
            floatingFilter: true,
            valueFormatter: numberFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'approvedDaysCount',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.approvedDate',
            }),
            hide: false,
            width: 85,
            filter: 'agSetColumnFilter',
            floatingFilter: true,
            valueFormatter: numberFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'unApprovedDaysCount',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.unApprovedDate',
            }),
            hide: false,
            width: 85,
            filter: 'agSetColumnFilter',
            floatingFilter: true,
            valueFormatter: numberFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'sentBackDaysCount',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.sentBackDate',
            }),
            hide: false,
            width: 85,
            filter: 'agSetColumnFilter',
            floatingFilter: true,
            valueFormatter: numberFormatter,
            cellStyle: numberCellStyle,
          },
        ],
      },
      // this Project time
      {
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.requestedTimeForThisProject',
        }),
        children: [
          {
            field: 'actualWorkHourThisProject',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.actualTime',
            }),
            hide: false,
            width: 75,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: floatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'requestedHourThisProject',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.requestedTime',
            }),
            hide: false,
            width: 75,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: floatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'unRequestedHourThisProject',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.unRequestedTime',
            }),
            hide: false,
            width: 85,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: floatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'approvedHourThisProject',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.approvedTime',
            }),
            hide: false,
            width: 85,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: floatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'unApprovedHourThisProject',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.unApprovedTime',
            }),
            hide: false,
            width: 85,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: floatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'sentBackHourThisProject',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.sentBackTime',
            }),
            hide: false,
            width: 85,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: floatFormatter,
            cellStyle: numberCellStyle,
          },
        ],
      },
      // resourcePlan
      {
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.resourcePlanActualDiff',
        }),
        children: [
          {
            field: 'plannedManPercent',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.resourcePlanEstimatedPercent',
            }),
            hide: false,
            width: 95,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: percentageFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'actualManPercent',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.resourcePlanActualPercent',
            }),
            hide: false,
            width: 95,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: percentageFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'plannedManHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.resourcePlanEstimatedHour',
            }),
            hide: false,
            width: 105,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: floatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'actualManHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.resourcePlanActualHour',
            }),
            hide: false,
            width: 105,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: floatFormatter,
            cellStyle: numberCellStyle,
          },
        ],
      },
    ],
  }
}
